
import React, { useEffect, useState, useMemo } from 'react';
import { Property } from '../types';
import { fetchInvestmentOpportunities } from '../services/ghlService';
import PropertyCard from '../components/PropertyCard';
import { Search, Loader2, SlidersHorizontal, ArrowUpDown } from 'lucide-react';

type SortOption = 'newest' | 'yield-high' | 'price-low' | 'price-high';

const ListingPage: React.FC = () => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('newest');

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await fetchInvestmentOpportunities();
        setProperties(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const filteredAndSortedProperties = useMemo(() => {
    let result = properties.filter(p => {
      const matchesFilter = filter === 'All' || p.dealType === filter;
      const matchesSearch = 
        p.town.toLowerCase().includes(searchQuery.toLowerCase()) || 
        p.postcodeOutcode.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.hook.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesFilter && matchesSearch;
    });

    // Sorting Logic
    switch (sortBy) {
      case 'yield-high':
        result.sort((a, b) => b.yieldPercent - a.yieldPercent);
        break;
      case 'price-low':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        result.sort((a, b) => b.price - a.price);
        break;
      default:
        // 'newest' - as provided by the API order
        break;
    }

    return result;
  }, [properties, filter, searchQuery, sortBy]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="animate-spin text-indigo-600" size={48} />
          <p className="text-slate-400 font-bold text-xs uppercase tracking-[0.3em]">Loading Opportunities</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Search and Filters */}
      <div className="mb-12 space-y-6">
        <div className="flex flex-col lg:flex-row gap-6 items-end justify-between">
          <div className="w-full lg:max-w-md relative group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors" size={18} />
            <input 
              type="text" 
              placeholder="Search town, postcode or reference..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-4 bg-white border border-slate-200 rounded-[1.25rem] focus:outline-none focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-500 transition-all shadow-sm text-sm"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
            <div className="relative">
              <select 
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="appearance-none pl-10 pr-10 py-3 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-600 focus:outline-none focus:ring-4 focus:ring-indigo-500/5 transition-all cursor-pointer uppercase tracking-widest"
              >
                <option value="newest">Latest Deals</option>
                <option value="yield-high">Highest Yield</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
              </select>
              <ArrowUpDown className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={14} />
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-4 border-t border-slate-100">
          <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
            {['All', 'BRRR', 'HMO', 'Flip', 'R2R', 'Buy to Let'].map((type) => (
              <button
                key={type}
                onClick={() => setFilter(type)}
                className={`px-5 py-2.5 rounded-xl text-[10px] font-black transition-all whitespace-nowrap uppercase tracking-[0.15em] ${
                  filter === type 
                    ? 'bg-slate-900 text-white shadow-xl shadow-slate-200' 
                    : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">
            {filteredAndSortedProperties.length} Opportunities Found
          </span>
        </div>
      </div>

      {/* Property Grid */}
      {filteredAndSortedProperties.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredAndSortedProperties.map(property => (
            <PropertyCard key={property.id} property={property} />
          ))}
        </div>
      ) : (
        <div className="text-center py-32 bg-white rounded-[3rem] border border-slate-100 shadow-sm">
          <div className="bg-slate-50 p-6 rounded-3xl inline-block mb-6">
            <SlidersHorizontal className="text-slate-300" size={40} />
          </div>
          <h3 className="text-xl font-black text-slate-900 mb-2">No Matching Deals</h3>
          <p className="text-slate-500 text-sm mb-8">Try adjusting your filters or search terms.</p>
          <button 
            onClick={() => {setFilter('All'); setSearchQuery(''); setSortBy('newest');}}
            className="px-8 py-3 bg-indigo-600 text-white font-black rounded-xl text-xs uppercase tracking-widest shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all"
          >
            Reset Filters
          </button>
        </div>
      )}
    </div>
  );
};

export default ListingPage;
